/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

// ============================================================================
// SHARED SHIPPING CALCULATION UTILITY
// ============================================================================
async function calculateShippingDates(shipdays) {
  // Fetch current server time from Shopify
  let serverTime;
  try {
    const response = await fetch(window.location.href, {
      method: 'HEAD',
      cache: 'no-store'
    });
    const dateHeader = response.headers.get('date');
    if (!dateHeader) {
      console.error('[SHIPPING CALC] No date header in response');
      return null;
    }
    serverTime = new Date(dateHeader);
    if (isNaN(serverTime.getTime())) {
      console.error('[SHIPPING CALC] Invalid date from server');
      return null;
    }
  } catch (e) {
    console.error('[SHIPPING CALC] Failed to get server time:', e);
    return null;
  }

  let futureDate = new Date(serverTime);

  // Get Pacific time parts — Intl handles DST automatically (same approach as countdown IIFE).
  // weekdayMap uses JS getDay() convention: Sun=0, Sat=6. Liquid %u is Mon=1, Sun=7 — different.
  const pacificFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
    hour: 'numeric',
    hour12: false
  });
  const parts = pacificFormatter.formatToParts(serverTime);
  const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  // Guard the part lookups (same as getPacificParts) so a missing Intl part falls
  // through to the null contract instead of throwing.
  const weekdayValue = parts.find(p => p.type === 'weekday')?.value;
  const hourValue = parts.find(p => p.type === 'hour')?.value;
  if (weekdayValue === undefined || hourValue === undefined) {
    console.error('[SHIPPING CALC] Missing Pacific time parts');
    return null;
  }
  const pacificWeekday = weekdayMap[weekdayValue];
  let pacificHour = parseInt(hourValue, 10);
  if (pacificHour === 24) pacificHour = 0; // Intl can return 24 for midnight

  // Read cutoff hours from shared config (D-01); defensive fallback to hardcoded defaults
  // in case scsShipConfig is missing from a broken deploy.
  const weekdayCutoff  = window.scsShipConfig?.weekdayCutoffHour  ?? 15;
  const saturdayCutoff = window.scsShipConfig?.saturdayCutoffHour ?? 11;

  // Sun=0: always past cutoff. Sat=6: 11 AM cutoff. Mon-Fri: 3 PM cutoff. (D-05)
  let afterCutoff = false;
  if (pacificWeekday === 0) {
    afterCutoff = true;
  } else if (pacificWeekday === 6) {
    afterCutoff = pacificHour >= saturdayCutoff;
  } else {
    afterCutoff = pacificHour >= weekdayCutoff;
  }
  if (afterCutoff) {
    // Order missed today's cutoff — start counting from tomorrow.
    // Advance past any weekend days to the next business day.
    futureDate.setDate(futureDate.getDate() + 1);
    while (futureDate.getDay() === 0 || futureDate.getDay() === 6) {
      futureDate.setDate(futureDate.getDate() + 1);
    }
  }

  let daysAdded = 0;

  while (daysAdded < shipdays) {
    futureDate.setDate(futureDate.getDate() + 1);
    const day = futureDate.getDay();
    if (day !== 0 && day !== 6) daysAdded++;
  }

  return {
    // Full month name to match the PO/manual tiers and the Liquid PDP renderer (%B %-d).
    shippingNotice: `${futureDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}`
  };
}



// Parse an ISO date string ("2026-07-15") using component splitting to avoid the UTC-midnight
// day shift that new Date(isoString) produces for Pacific customers.
// Returns "July 15" (full month name, no year) for display in "Ready to be shipped July 15".
// Returns null if the date is invalid.
function formatPoDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  if (isNaN(date.getTime())) return null;
  // Reject calendar-impossible dates that Date silently rolls over (e.g. 2026-02-30 => Mar 2).
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

// Past-date rule shared by the PO and manual tiers: a real ISO date on or after server-anchored
// Pacific today. Never console.error — an absent or stale date is normal for in-stock products.
function isUsableShipDate(iso, todayParts) {
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false;
  // Component-split parse to avoid UTC midnight shift (same rationale as formatPoDate).
  const [py, pm, pd] = iso.split('-').map(Number);
  // Regex admits out-of-range parts (2026-13-45, 2026-02-30); round-trip through Date to
  // reject any value it would silently roll over to a different calendar day.
  const probe = new Date(py, pm - 1, pd);
  if (probe.getFullYear() !== py || probe.getMonth() !== pm - 1 || probe.getDate() !== pd) return false;
  const { y, m, d } = todayParts;
  // Bail to false if the server-anchored today parts are unusable (missing Intl part => NaN),
  // matching the file's bail-to-safe-default convention rather than accepting a stale date.
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return false;
  // Date-only comparison against Pacific today: reject any date that has already passed.
  if (py < y) return false;
  if (py === y && pm < m) return false;
  if (py === y && pm === m && pd < d) return false;
  return true;
}

// Returns true only when a PO date is usable: source is "po", the ISO matches the expected
// format, and the date is not in the past relative to the server-anchored Pacific today.
// Never console.error — an absent or stale PO date is normal for in-stock products.
function isValidPoDate(iso, source, todayParts) {
  if (source !== 'po') return false;
  return isUsableShipDate(iso, todayParts);
}

// Fetches the server Date header and returns the Pacific calendar date as { y, m, d }.
// Falls back to the local clock if the HEAD request fails, consistent with the
// cutoff-countdown degradation pattern (never throw; always return a usable date).
async function getServerPacificToday() {
  let ref;
  try {
    const resp = await fetch(window.location.href, { method: 'HEAD', cache: 'no-store' });
    const dateHeader = resp.headers.get('date');
    ref = dateHeader ? new Date(dateHeader) : new Date();
    if (isNaN(ref.getTime())) ref = new Date();
  } catch (e) {
    console.error('[SHIPPING CALC] Failed to get server time for PO date check:', e);
    ref = new Date();
  }
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const parts = fmt.formatToParts(ref);
  const get = type => Number(parts.find(p => p.type === type)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
}


// main atc

(() => {
  const dpContainer = document.querySelector('[data-damage-protection-container]');
  const dpCheckbox = dpContainer ? document.getElementById('damage-protection-double') : null;
  
  let dpVariantId = dpCheckbox?.getAttribute('data-dp-variant') || null;
  if (!dpVariantId && dpCheckbox?.checked) {
    console.warn('[DP] data-dp-variant missing! Check your HTML.');
  }

  const tuneContainer = document.querySelector('#component-lube-service');
  const getTuneId = () => {
    if (!tuneContainer) return null;
    const select = document.getElementById('lube-service-select');
    if (select && select.value && select.value !== 'none') return select.value;
    const radio = document.querySelector('input[name="setup_service"]:checked');
    return radio && radio.value !== 'none' ? radio.value : null;
  };

  const pvcContainer = document.querySelector('#content-pvc-coating');
  const getPvcId = () => {
    if (!pvcContainer) return null;
    const checkbox = document.getElementById('pvc-coating-checkbox');
    if (!checkbox || !checkbox.checked) return null;
    return checkbox.dataset.pid || null;
  };

  // PDP context: services (PVC/Tune/DP) and the captured form's variant input.
  // May be null on multi-product pages (collections/homepage) — interceptor handles that.
  const pdpForm = dpContainer?.closest('form') || tuneContainer?.closest('form') || pvcContainer?.closest('form') || null;
  const pdpVariantInput = pdpForm?.querySelector('[name="id"]') || null;

  document.addEventListener('DOMContentLoaded', () => {
    const dpLabel = document.querySelector('label[for="damage-protection-double"]');
    if (dpCheckbox && dpLabel) {
      dpCheckbox.addEventListener('change', () => {
        dpLabel.classList.toggle('is-selected', dpCheckbox.checked);
      });
    }
  });

  // Guard: prevents a double-click from firing two concurrent bundle-add flows.
  // Clears in finally so a failed or early-exit add never locks subsequent ATCs.
  let atcInFlight = false;

  const originalFetch = window.fetch;
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input.url;
    if (!url.includes('/cart/add.js')) return originalFetch(input, init);

    // If a bundle add is already in flight, pass this one through unmodified.
    // The base theme BuyButton disables its own button, but quick-buy and raw
    // form submits can still fire a second /cart/add.js before the first settles.
    // That pass-through means no Ship Date, no _shipdays, no _backorder on the
    // line — real order data, silently missing. The OOS suggestion card put a
    // second add-to-cart control on the PDP above the buy button, so the
    // collision is reachable by ordinary customer behavior now; log it so it
    // can be caught in the wild instead of inferred from a cart line later.
    if (atcInFlight) {
      console.warn('[SERVICES] Concurrent cart-add — passed through unstamped', url);
      return originalFetch(input, init);
    }
    atcInFlight = true;

    try {
    let payload;
    try {
      payload = init?.body ? JSON.parse(init.body) : null;
    } catch (e) {
      console.error('[SERVICES] JSON parse failed:', e);
      return originalFetch(input, init);
    }
    if (!payload) return originalFetch(input, init);

    const items = payload.items || [{
      id: payload.id,
      quantity: payload.quantity || 1,
      properties: payload.properties || {}
    }];

    // Pick the "main" item: the one matching the PDP form (when on a PDP), else the first item.
    const mainItem = (pdpVariantInput && items.find(i => String(i.id) === String(pdpVariantInput.value))) || items[0];
    if (!mainItem) return originalFetch(input, init);

    // The Mystery Gift is stamped by scs-free-gift.js, not this interceptor — no Ship
    // Date, no ship tiers. Pass it through untouched; the reconciler handles cart:refresh.
    if (mainItem.properties?._free_gift) return originalFetch(input, init);

    // PDP-context check: only apply PVC/Tune/DP service add-ons when the ATC is for the PDP's variant.
    const isPdpAtc = !!(pdpVariantInput && String(mainItem.id) === String(pdpVariantInput.value));

    let modified = false;
    let productSku = ''; // fallback

    // Resolve product handle: form-first — the form carrying this ATC's variant input owns the
    // item, wherever the add came from. URL is the fallback; safe because the ship-data endpoint
    // no longer substitutes a variant that isn't the requested product's.
    let handle = null;
    const variantInputEl = document.querySelector(
      `form[action="/cart/add"] input[name="id"][value="${CSS.escape(String(mainItem.id))}"]`
    );
    const formForVariant = variantInputEl?.closest('form');
    // The OOS suggestion widget's mount point (product-info.liquid) sits inside this
    // same <product-form>, and its card carries its own [data-product-handle] for the
    // SUGGESTED product. A plain querySelector here would match that nested handle
    // before ever reaching the PDP's own — there is none on the PDP's own markup, the
    // attribute only exists on off-PDP card forms (product-item.liquid) and the
    // widget's card — so any add of the PDP's own variant while a card happens to be
    // mounted would silently stamp ship data for the wrong product. Skip handles
    // nested inside the widget's container; product-item.liquid's off-PDP forms never
    // contain one, so this exclusion is a no-op there.
    const handleCandidates = formForVariant
      ? Array.from(formForVariant.querySelectorAll('[data-product-handle]')).filter(
          (el) => !el.closest('[data-scs-oos-container]')
        )
      : [];
    // The exclusion above is only sound while the PDP's own form carries no
    // handle of its own. Nothing enforces that: a future block, an app embed,
    // or a nested {% form %} whose inner tag the HTML parser drops would orphan
    // a second handle into this form and silently stamp ship data for the wrong
    // product. One candidate is the expected shape everywhere — say so out loud
    // when it isn't, since a wrong handle is otherwise indistinguishable from a
    // right one downstream.
    if (handleCandidates.length > 1) {
      console.warn(
        '[SERVICES] Multiple product handles in one cart-add form — using the first',
        handleCandidates.map((el) => el.dataset.productHandle)
      );
    }
    const handleEl = handleCandidates[0] || null;
    if (handleEl) handle = handleEl.dataset.productHandle;
    if (!handle) {
      const pathParts = window.location.pathname.split('/');
      const productsIndex = pathParts.indexOf('products');
      if (productsIndex !== -1 && pathParts.length > productsIndex + 1) {
        handle = pathParts[productsIndex + 1].split('?')[0];
      }
    }
    if (!handle) {
      console.warn('[SERVICES] Unresolved product handle for ATC — ship-data skipped', mainItem.id);
    }

    let preOrderShipDate = null;
    let shipDaysOverride = 0;
    let expectedShipDate = null;
    let shipDateSource = null;

    if (handle) {
      try {
        // &variant ensures product.selected_variant resolves the exact SKU being added,
        // which populates expected_ship_date and ship_date_source (Phase 3 D-04/D-12).
        const shipDataResp = await fetch(`/products/${handle}?view=ship-data&variant=${mainItem.id}`, { cache: 'no-store' });
        if (shipDataResp.ok) {
          const data = JSON.parse((await shipDataResp.text()).trim());
          preOrderShipDate = data.pre_order_ship_date || null;
          shipDaysOverride = Number(data.ship_days_override) || 0;
          expectedShipDate = data.expected_ship_date || null;
          shipDateSource = data.ship_date_source || null;
        }
      } catch (e) {
        console.error('[SERVICES] Failed to fetch ship metafields', e);
      }
    }

    let isBackorder = false;
    let inventoryKnown = false; // true once the live variant read succeeds, so a failed read can fall back to flag-trust
    try {
      const variantResp = await fetch(`/variants/${mainItem.id}.js`);
      if (variantResp.ok) {
        const variantData = await variantResp.json();
        inventoryKnown = true;

        productSku = variantData.sku || '';

        isBackorder =
          variantData.inventory_management &&
          variantData.inventory_quantity <= 0 &&
          variantData.inventory_policy === 'continue';
      } else {
        console.error('Failed to fetch variant info', variantResp.status);
      }
    } catch (e) {
      console.error('Error fetching variant info', e);
    }

    mainItem.properties = mainItem.properties || {};

    // Tier 1 — live PO date. Gated on the producer's source flag PLUS a live backorder
    // re-check when inventory is known, so a stale "po" flag on a just-restocked variant
    // can't stamp a PO date in the cart — matches the PDP inventory gate + PODATE-05
    // ("in-stock SKU never shows a PO date"). If the variant read failed (inventoryKnown
    // false), fall back to trusting the flag so a network blip never drops a genuine
    // backorder's PO date (the D-04/D-05 resilience intent).
    // The PO date wins over the manual pre_order_ship_date, override, and 7-day default (D-07).
    let poWon = false;
    let todayParts = null;
    try {
      // Cheap pre-check before the HEAD request: only PO-sourced products with a date can win
      // Tier 1, so non-PO ATCs (the common case) skip the server round-trip entirely. Also
      // avoids a duplicate HEAD when a lower tier later calls calculateShippingDates.
      if (shipDateSource === 'po' && expectedShipDate && (!inventoryKnown || isBackorder)) {
        todayParts = await getServerPacificToday();
        if (isValidPoDate(expectedShipDate, shipDateSource, todayParts)) {
          const poFormatted = formatPoDate(expectedShipDate);
          if (poFormatted) {
            mainItem.properties['Ship Date'] = (window.rtbs || '') + ' ' + poFormatted;
            mainItem.properties['_backorder'] = 1; // a valid "po" date always implies backordered
            mainItem.properties['_ship_date_source'] = 'po';
            mainItem.properties['_expected_ship_date'] = expectedShipDate;
            modified = true;
            poWon = true;
          }
        }
      }
    } catch (e) {
      console.error('[SERVICES] PO date check failed:', e);
      // fall through to manual/override/7-day tiers
    }

    // A past manual date is treated as absent under the PO tier's rule, so the line falls
    // through to the override or the no-date message.
    let manualWon = false;
    if (!poWon && preOrderShipDate) {
      try {
        if (!todayParts) todayParts = await getServerPacificToday();
        manualWon = isUsableShipDate(preOrderShipDate, todayParts);
      } catch (e) {
        console.error('[SERVICES] Manual date check failed:', e);
        // fall through to override/no-date tiers
      }
    }

    if (poWon) {
      // Tiers 2-4 are bypassed when the PO date wins.
    } else if (manualWon) {
      // Tier 2 — manual pre_order_ship_date.
      // custom.pre_order_ship_date is a date-type field, always ISO YYYY-MM-DD.
      // The || fallback degrades to the raw value if a stored date rolled over.
      const formattedManual = formatPoDate(preOrderShipDate) || preOrderShipDate;
      mainItem.properties['Ship Date'] = (window.rtbs || '') + ' ' + formattedManual;
      mainItem.properties['_backorder'] = isBackorder ? 1 : 0;
      // Marker so cart-update and service-override paths don't recalculate over a fixed date.
      // _pre_order_ship_date stays RAW (ISO or free-text) — not the formatted display value.
      mainItem.properties['_pre_order_ship_date'] = preOrderShipDate;
      modified = true;
    } else {
      // Tiers 3+4 — ship_days_override REPLACES the 7-day backorder default. The override is the
      // merchant's authoritative ship time for this product regardless of stock state.
      let shipdays = 0;
      if (shipDaysOverride > 0) {
        shipdays = shipDaysOverride;
      } else if (isBackorder) {
        // no PO / manual / override on an oversell-ON backorder: stamp the honest message,
        // no fabricated 7-day date; bare string, no rtbs prefix — mirrors scs-backorder-shipdays.liquid.
        mainItem.properties['Ship Date'] = 'We do not have an in-stock date at this time';
        mainItem.properties['_backorder'] = 1;
        mainItem.properties['_no_ship_date'] = 1;
        modified = true;
      }

      if (shipdays > 0) {
        const dates = await calculateShippingDates(shipdays);
        mainItem.properties['_shipdays'] = shipdays;
        mainItem.properties['_backorder'] = isBackorder ? 1 : 0;
        if (dates) {
          mainItem.properties['Ship Date'] = (window.rtbs || '') + ' ' + dates.shippingNotice;
        }
        modified = true;
      }
    }

    // Service add-ons (PVC, Pro Tune, Damage Protection) only apply for PDP ATCs:
    // they read PDP-only DOM state (checkboxes, toggles) and pair child items to the main item.
    if (isPdpAtc) {
      const parentQuantity = mainItem.quantity;

      const pvcCheckbox = document.getElementById('pvc-coating-checkbox');
      let pvcOverride = null;
      if (pvcCheckbox?.checked) {
        const overrideAttr = pvcCheckbox.getAttribute('data-ship-days-override');
        if (overrideAttr) {
          pvcOverride = Number(overrideAttr);
        }
      }

      const proTuneChecked = document.getElementById('pro-tune-toggle')?.checked;
      const tuneEl = document.querySelector('input[name="setup_service"]:checked');
      const tuneLabel = tuneEl?.closest('label');
      const tuneOverride = proTuneChecked && tuneLabel
        ? Number(tuneLabel.dataset.shipDaysOverride || 0)
        : null;

      // Resolve which service variants will actually be attached. Stamp _productid on the main
      // line item ONLY when at least one service is being added — a unique _productid on every
      // ATC breaks Shopify's line-item merging, so adding the same product twice produces two
      // lines instead of incrementing quantity.
      const tuneId = proTuneChecked ? getTuneId() : null;
      const pvcId = getPvcId();
      const dpEnabled = !!(dpCheckbox?.checked && dpVariantId);
      const willAttachServices = !!(tuneId || pvcId || dpEnabled);

      let pairId = null;
      if (willAttachServices) {
        // Deterministic id: same product + same services => same _productid, so Shopify
        // native line-item merging collapses duplicate bundles. Old Date.now() id stamped
        // a fresh id per click, causing the 4-lines-instead-of-2 bug (A2).
        // Different service set => different sort => different id => new line pair (D-05).
        pairId = `svc_${mainItem.id}_${[tuneId, pvcId, (dpEnabled ? dpVariantId : null)]
          .filter(Boolean).sort().join('-')}`;
        mainItem.properties['_productid'] = pairId;
      }

      const serviceOverrides = [];
      if (pvcOverride !== null && !isNaN(pvcOverride)) serviceOverrides.push(pvcOverride);
      if (tuneOverride !== null && !isNaN(tuneOverride)) serviceOverrides.push(tuneOverride);

      if (serviceOverrides.length > 0) {
        const maxServiceDays = Math.max(...serviceOverrides);
        const baseShipdays = mainItem.properties['_shipdays'] || 0;
        const finalMainShipdays = baseShipdays + maxServiceDays;

        // Fixed-date wins: skip recalc when a current manual date, PO date, or no-date
        // message is the winning outcome. Services do not add days to a fixed/no-date line
        // in v1 (PODATE-07, DEFER-01, D-06).
        const hasFixedDate = manualWon
          || mainItem.properties['_ship_date_source'] === 'po'
          || !!mainItem.properties['_no_ship_date'];
        if (!hasFixedDate) {
          mainItem.properties['_shipdays'] = finalMainShipdays;
          const dates = await calculateShippingDates(finalMainShipdays);
          if (dates) {
            mainItem.properties['Ship Date'] = (window.rtbs || '') + ' ' + dates.shippingNotice;
          }
        }

        modified = true;
      }

      if (tuneId) {
        const tuneItem = {
          id: tuneId,
          quantity: parentQuantity,
          properties: {
            'SKU To Apply': productSku,
            '_Apply To': pairId
          }
        };
        if (tuneOverride !== null && !isNaN(tuneOverride)) {
          tuneItem.properties['_shipdays'] = tuneOverride;
        }
        items.unshift(tuneItem);
        modified = true;
      }

      if (dpEnabled) {
        items.unshift({
          id: dpVariantId,
          quantity: parentQuantity,
          properties: {
            'SKU To Apply': productSku,
            '_Apply To': pairId
          }
        });
        modified = true;
      } else if (dpCheckbox?.checked) {
        console.error('[DP] NOT ADDED: dpVariantId is missing or null');
      }

      if (pvcId) {
        const pvcItem = {
          id: pvcId,
          quantity: parentQuantity,
          properties: {
            'SKU To Apply': productSku,
            '_Apply To': pairId
          }
        };
        if (pvcOverride !== null && !isNaN(pvcOverride)) {
          pvcItem.properties['_shipdays'] = pvcOverride;
        }
        items.unshift(pvcItem);
        modified = true;
      }
    }

    if (!modified) return originalFetch(input, init);
    const newInit = { ...init, body: JSON.stringify({ items }) };
    let resp;
    try {
      resp = await originalFetch(input, newInit);
    } catch (e) {
      console.error('[SERVICES] Fetch failed:', e);
      return resp;
    }

    if (!resp.ok) {
      console.error('[SERVICES] Add failed:', await resp.clone().json().catch(() => 'Unknown error'));
    } else {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    }

    return resp;
    } finally {
      atcInFlight = false;
    }
  };
})();


// update
// Handles service orphan cleanup and quantity sync only. Never writes Ship Date or
// recalculates ship dates — so PO and manual fixed dates can never be overwritten here.

(() => {
  const originalFetch = window.fetch;
  window.fetch = async (input, init) => {
    const url = typeof input === 'string' ? input : input.url;
    if (!url.includes('/cart/change.js') && !url.includes('/cart/update.js')) {
      return originalFetch(input, init);
    }

    // === GET CURRENT CART ===
    let cart;
    try {
      const resp = await originalFetch('/cart.js');
      if (!resp.ok) throw new Error('Cart fetch failed');
      cart = await resp.json();
    } catch (e) {
      console.error('[CART-CLEANUP] Failed to get cart:', e);
      return originalFetch(input, init);
    }

    // === MAP: Apply To → [service keys] ===
    // The Mystery Gift never carries _Apply To, but skip it explicitly so it's never
    // mistaken for a service line by this map.
    const serviceMap = new Map();
    cart.items.forEach(item => {
      if (item.properties?.['_free_gift']) return;
      const applyTo = item.properties?.['_Apply To'];
      if (applyTo) {
        if (!serviceMap.has(applyTo)) serviceMap.set(applyTo, []);
        serviceMap.get(applyTo).push(item.key);
      }
    });

    // === PARSE PAYLOAD ===
    let payload;
    try {
      payload = init?.body ? JSON.parse(init.body) : null;
    } catch (e) {
      console.error('[CART-CLEANUP] Payload parse failed:', e);
      return originalFetch(input, init);
    }
    if (!payload) return originalFetch(input, init);

    const changes = {};
    let isRemoveRequest = false;
    let targetLineKey = null;

    if (url.includes('/cart/change.js')) {
      if (payload.line !== undefined && payload.quantity !== undefined) {
        const lineIndex = payload.line - 1;
        if (cart.items[lineIndex]) {
          targetLineKey = cart.items[lineIndex].key;
          changes[targetLineKey] = payload.quantity;
          if (payload.quantity === 0) isRemoveRequest = true;
        }
      } else if (payload.id && payload.quantity !== undefined) {
        targetLineKey = payload.id;
        changes[targetLineKey] = payload.quantity;
        if (payload.quantity === 0) isRemoveRequest = true;
      }
    } else if (url.includes('/cart/update.js') && payload.updates) {
      Object.entries(payload.updates).forEach(([key, qty]) => {
        changes[key] = qty;
        if (qty === 0) isRemoveRequest = true;
      });
    }

    if (Object.keys(changes).length === 0) return originalFetch(input, init);

    // === BUILD FULL CLEANUP ===
    const fullCleanup = { ...changes };

    for (const [lineKey, newQty] of Object.entries(changes)) {
      const item = cart.items.find(i => i.key === lineKey);
      if (!item) continue;
      if (item.properties?.['_free_gift']) continue; // never swept as an orphan service line

      const parentId = item.properties?.['_productid'];
      if (!parentId) continue;

      const services = serviceMap.get(parentId) || [];
      if (newQty === 0) {
        services.forEach(key => { fullCleanup[key] = 0; });
      } else if (newQty > 0) {
        services.forEach(key => {
          const svc = cart.items.find(i => i.key === key);
          if (svc && svc.quantity !== newQty) {
            fullCleanup[key] = newQty;
          }
        });
      }
    }

    // === APPLY CLEANUP ===
    if (Object.keys(fullCleanup).length > 0) {
      let cleanupResp;
      try {
        cleanupResp = await originalFetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: fullCleanup })
        });
      } catch (e) {
        console.error('[CART-CLEANUP] Cleanup fetch failed:', e);
        return originalFetch(input, init);
      }

      if (!cleanupResp.ok) {
        console.error('[CART-CLEANUP] Cleanup failed:', await cleanupResp.clone().json().catch(() => 'Unknown error'));
      } else {
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      }
    }

    if (isRemoveRequest && targetLineKey && fullCleanup[targetLineKey] === 0) {
      return new Response(JSON.stringify({}), { status: 200 });
    }

    return originalFetch(input, init);
  };
})();






// product page shipdays
// ============================================================================
// INVENTORY MESSAGE TOGGLER - Shows/hides inventory spans based on services
// ============================================================================
(() => {
  // Only run on product pages
  if (typeof theme === 'undefined' || theme.pageType !== 'product') return;

  const pvcCheckbox = document.getElementById('pvc-coating-checkbox');
  const tuneCheckbox = document.getElementById('pro-tune-toggle');
  const tuneRadios = document.querySelectorAll('input[name="setup_service"]');

  const baseSpan = document.querySelector('[data-inventory-base]');
  const pvcSpan = document.querySelector('[data-inventory-with-pvc]');
  const tuneSpan = document.querySelector('[data-inventory-with-tune]');
  const bothSpan = document.querySelector('[data-inventory-with-both]');

  if (!baseSpan) return;

  const updateInventoryDisplay = () => {
    // Check PVC checkbox
    const hasPvc = pvcCheckbox && pvcCheckbox.checked;

    // Check Tune/Lube service selection - ONLY checkbox now
    let hasTune = false;
    if (tuneCheckbox) {
      hasTune = tuneCheckbox.checked;
    }

    // Hide all spans first
    baseSpan.style.display = 'none';
    if (pvcSpan) pvcSpan.style.display = 'none';
    if (tuneSpan) tuneSpan.style.display = 'none';
    if (bothSpan) bothSpan.style.display = 'none';

    // Show appropriate span
    if (hasPvc && hasTune && bothSpan) {
      bothSpan.style.display = '';
    } else if (hasPvc && pvcSpan) {
      pvcSpan.style.display = '';
    } else if (hasTune && tuneSpan) {
      tuneSpan.style.display = '';
    } else {
      baseSpan.style.display = '';
    }
  };

  // Attach listeners
  if (pvcCheckbox) {
    pvcCheckbox.addEventListener('change', updateInventoryDisplay);
  }

  if (tuneCheckbox) {
    tuneCheckbox.addEventListener('change', updateInventoryDisplay);
  }

  // Listen for variant changes
  document.addEventListener('variant:changed', () => {
    // Wait for DOM update, then recheck
    setTimeout(updateInventoryDisplay, 50);
  });

  // Initial check
  updateInventoryDisplay();
})();


/******
 * Shipping protection
 */
(function() {
  'use strict';

  const CONFIG = {
    navidiumProductHandle: 'navidium-shipping-protection',
    cleanupWaitMs: 3000,
    reanchorWaitMs: 5000
  };

  let pageCleanup = null;

  // one copy of each, read by attachHandlers and the pageshow restore
  const protectedSelector = 'button.checkout-with-protection[type="submit"], input.checkout-with-protection[type="submit"]';
  const plainSelector = 'button[name="checkout"]:not(.checkout-with-protection), input[name="checkout"]:not(.checkout-with-protection)';

  async function getCartData() {
    try {
      const res = await fetch('/cart.js');
      return await res.json();
    } catch (e) {
      console.error('Cart fetch failed:', e);
      return null;
    }
  }

  // The price is decided in snippets/scs-protection-quote.liquid and rendered on the button.
  function readQuote(el) {
    if (!el || !el.dataset) return null;
    const variantId = Number(el.dataset.protectionVariantId);
    if (!Number.isInteger(variantId) || variantId <= 0) return null;
    return {
      variantId,
      price: Number(el.dataset.protectionPrice),
      sig: el.dataset.protectionSig || ''
    };
  }

  // Same string the quote snippet renders into data-protection-sig. A mismatch only costs one extra read.
  function cartSignature(cart) {
    const tokens = cart.items
      .filter(item => item.handle !== CONFIG.navidiumProductHandle)
      .map(item => item.variant_id + ':' + item.quantity + ':' + (item.final_line_price === 0 ? 0 : 1));
    return cart.currency + '~' + tokens.join(',');
  }

  async function fetchFreshQuote() {
    try {
      const url = ((window.routes && window.routes.cartUrl) || '/cart') + '?section_id=mini-cart';
      const res = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
      if (!res.ok) throw new Error('Section read answered ' + res.status);
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      return readQuote(doc.querySelector('.checkout-with-protection'));
    } catch (e) {
      console.error('[PROTECTION] quote refresh failed:', e);
      return null;
    }
  }

  async function removeProtection(variantId) {
    if (!variantId) return;
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 0 })
      });
    } catch (e) {
      console.warn('Remove protection failed:', e);
    }
  }

  async function addProtection(variantId) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1 })
    });
    if (!res.ok) throw new Error('Add protection answered ' + res.status);
  }

  // NEW: Remove any existing protection on page load
  async function clearExistingProtectionOnInit() {
    try {
      const cart = await getCartData();
      if (!cart) return;

      const protectionItems = cart.items.filter(item => 
        item.handle === CONFIG.navidiumProductHandle
      );

      if (protectionItems.length > 0) {
        console.log('Removing existing shipping protection on init');
        for (const item of protectionItems) {
          await removeProtection(item.variant_id);
        }
      }
    } catch (err) {
      console.error('Failed to clear protection on init:', err);
    }
  }

  // EMAIL-03 re-anchor (D-08). A _shipdays-derived Ship Date stamped on the add-to-cart day
  // goes stale if the cart sits across a day boundary. Re-derive it from the stored _shipdays
  // against current server time before the order is created so the order/email date is current.
  // Fixed dates (PO, manual ISO, no-date) never move (D-09).

  // Un-monkeypatched fetch so the property patch never re-enters the cart-update interceptor.
  let _nativeFetch = null;
  function nativeFetch(input, init) {
    if (!_nativeFetch) {
      const frame = document.createElement('iframe');
      frame.style.display = 'none';
      document.body.appendChild(frame);
      _nativeFetch = frame.contentWindow.fetch.bind(window);
      frame.remove();
    }
    return _nativeFetch(input, init);
  }

  // Same skip set as the cart-add service path (D-09): PO, manual, and no-date lines are fixed.
  function hasFixedDate(props) {
    return props['_ship_date_source'] === 'po'
      || (props['_pre_order_ship_date'] != null && props['_pre_order_ship_date'] !== '')
      || (props['_no_ship_date'] != null && props['_no_ship_date'] !== '');
  }

  // Checkout backstop: compute the Ship Date properties for a line that arrived with none
  // (an add that bypassed the /cart/add.js interceptor — express/dynamic checkout or a quick-add).
  // Mirrors the cart-add tier logic byte-for-byte (PO -> manual -> override -> oversell fallback ->
  // in-stock no-stamp) so the order/email agrees with the PDP + cart. Returns a properties patch,
  // or null for an in-stock line that should carry no ship date.
  async function computeShipDateProps(handle, variantId) {
    let preOrderShipDate = null, shipDaysOverride = 0, expectedShipDate = null, shipDateSource = null;
    try {
      const r = await fetch(`/products/${handle}?view=ship-data&variant=${variantId}`, { cache: 'no-store' });
      if (r.ok) {
        const d = await r.json();
        preOrderShipDate = d.pre_order_ship_date || null;
        shipDaysOverride = Number(d.ship_days_override) || 0;
        expectedShipDate = d.expected_ship_date || null;
        shipDateSource = d.ship_date_source || null;
      }
    } catch (e) { console.error('[SERVICES] backstop ship-data fetch failed:', e); }

    let isBackorder = false, inventoryKnown = false;
    try {
      const vr = await fetch(`/variants/${variantId}.js`);
      if (vr.ok) {
        const vd = await vr.json();
        inventoryKnown = true;
        isBackorder = vd.inventory_management && vd.inventory_quantity <= 0 && vd.inventory_policy === 'continue';
      }
    } catch (e) { console.error('[SERVICES] backstop variant fetch failed:', e); }

    // Tier 1 — live PO date (same gate as cart-add: source flag + live backorder re-check).
    let todayParts = null;
    if (shipDateSource === 'po' && expectedShipDate && (!inventoryKnown || isBackorder)) {
      try {
        todayParts = await getServerPacificToday();
        if (isValidPoDate(expectedShipDate, shipDateSource, todayParts)) {
          const f = formatPoDate(expectedShipDate);
          if (f) return { 'Ship Date': (window.rtbs || '') + ' ' + f, '_backorder': 1, '_ship_date_source': 'po', '_expected_ship_date': expectedShipDate };
        }
      } catch (e) { console.error('[SERVICES] backstop PO check failed:', e); }
    }
    // Tier 2 — manual pre_order_ship_date (ISO). Past dates fall through, same rule as tier 1.
    if (preOrderShipDate) {
      try {
        if (!todayParts) todayParts = await getServerPacificToday();
        if (isUsableShipDate(preOrderShipDate, todayParts)) {
          const f = formatPoDate(preOrderShipDate) || preOrderShipDate;
          return { 'Ship Date': (window.rtbs || '') + ' ' + f, '_backorder': isBackorder ? 1 : 0, '_pre_order_ship_date': preOrderShipDate };
        }
      } catch (e) { console.error('[SERVICES] backstop manual check failed:', e); }
    }
    // Tier 3 — ship_days_override (relative).
    if (shipDaysOverride > 0) {
      const dates = await calculateShippingDates(shipDaysOverride);
      const patch = { '_shipdays': shipDaysOverride, '_backorder': isBackorder ? 1 : 0 };
      if (dates) patch['Ship Date'] = (window.rtbs || '') + ' ' + dates.shippingNotice;
      return patch;
    }
    // Tier 4 — oversell backorder, no signals: the honest message (mirrors scs-backorder-shipdays.liquid).
    if (isBackorder) {
      return { 'Ship Date': 'We do not have an in-stock date at this time', '_backorder': 1, '_no_ship_date': 1 };
    }
    // In-stock, no signals — no stamp.
    return null;
  }

  // Every stamp a fixed-date line can carry. A restamp clears them all, then the patch from
  // computeShipDateProps puts back whatever still applies.
  const SHIP_STAMP_KEYS = [
    'Ship Date', '_backorder', '_pre_order_ship_date',
    '_ship_date_source', '_expected_ship_date', '_no_ship_date', '_shipdays'
  ];

  async function reanchorRelativeDates() {
    try {
      const cart = await getCartData();
      if (!cart || !cart.items) return;
      let todayParts = null;
      for (const item of cart.items) {
        const props = item.properties || {};
        const hasShipDate = props['Ship Date'] != null && props['Ship Date'] !== '';

        // Backstop: a line with no Ship Date was added via a path that skipped the cart-add
        // interceptor. Compute + stamp it now so the order/email carries the right date. Service
        // lines derive their date from the main line — never stamp them here.
        if (!hasShipDate) {
          if (props['SKU To Apply'] || props['_Apply To'] || props['_free_gift']) continue;
          const patch = await computeShipDateProps(item.handle, item.id);
          if (patch) {
            const merged = { ...props, ...patch };
            await nativeFetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: item.key, properties: merged })
            });
          }
          continue;
        }

        // A manual or PO date stamped earlier can pass while the cart sits. It would ride to the
        // order as a past date, so restamp it from current metafields. Same rule as add-to-cart.
        const stampedDate = props['_pre_order_ship_date']
          || (props['_ship_date_source'] === 'po' ? props['_expected_ship_date'] : null);
        if (stampedDate != null && stampedDate !== '') {
          if (!todayParts) todayParts = await getServerPacificToday();
          if (!isUsableShipDate(String(stampedDate), todayParts)) {
            const patch = await computeShipDateProps(item.handle, item.id);
            const merged = { ...props };
            SHIP_STAMP_KEYS.forEach((k) => { delete merged[k]; });
            if (patch) Object.assign(merged, patch);
            await nativeFetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: item.key, properties: merged })
            });
            continue;
          }
        }

        // Existing relative re-anchor for already-stamped lines. Fixed dates never move (D-09).
        if (hasFixedDate(props)) continue;
        const shipdays = Number(props['_shipdays']);
        if (!(shipdays > 0)) continue;
        const dates = await calculateShippingDates(shipdays);
        if (!dates) continue;
        const newShipDate = (window.rtbs || '') + ' ' + dates.shippingNotice;
        if (props['Ship Date'] === newShipDate) continue; // already current — no write
        // A2 verdict is REPLACE: /cart/change.js wipes any property not sent, so merge the
        // new Ship Date into the full existing properties object before patching.
        const merged = { ...props, 'Ship Date': newShipDate };
        await nativeFetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: item.key, properties: merged })
        });
      }
    } catch (err) {
      console.error('[SERVICES] Re-anchor failed:', err);
    }
  }

  // The re-anchor is a courtesy to the order's date label. A request that never comes back must
  // not hold the customer on a disabled button, so checkout goes ahead once the wait runs out.
  async function reanchorWithinLimit() {
    let timer;
    const limit = new Promise(resolve => { timer = setTimeout(() => resolve('timeout'), CONFIG.reanchorWaitMs); });
    const result = await Promise.race([reanchorRelativeDates(), limit]);
    clearTimeout(timer);
    if (result === 'timeout') console.warn('[SERVICES] re-anchor still running after the wait limit, going to checkout');
  }

  async function handleProtectedCheckout(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const button = event.currentTarget;
    const originalText = button.innerHTML;
    //button.innerHTML = 'Adding protection...';
    button.disabled = true;

    try {
      if (pageCleanup) {
        // the cleanup removes protection by variant id, so it would delete a line the click just added
        let timer;
        await Promise.race([pageCleanup, new Promise(resolve => { timer = setTimeout(resolve, CONFIG.cleanupWaitMs); })]);
        clearTimeout(timer);
      }

      const cart = await getCartData();
      if (!cart) throw new Error('Cart unavailable');

      let quote = readQuote(button);
      if (!quote || quote.sig !== cartSignature(cart)) {
        console.warn('[PROTECTION] button quote missing or older than the cart, re-reading');
        const fresh = await fetchFreshQuote();
        if (fresh) quote = fresh;
      }
      if (!quote) throw new Error('No protection quote');

      const lines = cart.items.filter(item => item.handle === CONFIG.navidiumProductHandle);
      const alreadyThere = lines.length === 1
        && lines[0].variant_id === quote.variantId
        && lines[0].quantity === 1;

      if (!alreadyThere) {
        for (const line of lines) {
          await removeProtection(line.variant_id);
        }
        console.log('[PROTECTION] adding variant', quote.variantId);
        await addProtection(quote.variantId);
      }

      localStorage.setItem('nvd_opted_in', 'true');

      // Re-anchor relative ship dates before the order is created (EMAIL-03, D-08a).
      await reanchorWithinLimit();

      window.location.href = '/checkout';

    } catch (err) {
      console.error('[PROTECTION] protected checkout failed:', err);
      alert('Could not add protection. Proceeding to normal checkout?');
      window.location.href = '/checkout';
    }
  }

  // Opt-out / unprotected checkout paths (D-08a all-paths). Covers .checkout-without-protection
  // and the bare button[name="checkout"] rendered when protection is disabled. Re-anchor, then go.
  async function handlePlainCheckout(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const button = event.currentTarget;
    button.disabled = true;
    await reanchorWithinLimit();
    window.location.href = '/checkout';
  }

  function attachHandlers() {
    document.querySelectorAll(protectedSelector).forEach(btn => {
      if (btn.dataset.protectionHandled) return;
      btn.dataset.protectionHandled = 'true';

      btn.addEventListener('click', handleProtectedCheckout, true);

      const form = btn.closest('form');
      if (form) {
        form.addEventListener('submit', e => {
          if (e.submitter === btn) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        }, true);
      }
    });

    // Opt-out + bare checkout buttons (D-08a all-paths): every name="checkout" submit that is
    // NOT the protected button still needs the relative-date re-anchor before /checkout.
    document.querySelectorAll(plainSelector).forEach(btn => {
      if (btn.dataset.reanchorHandled) return;
      btn.dataset.reanchorHandled = 'true';

      btn.addEventListener('click', handlePlainCheckout, true);

      const form = btn.closest('form');
      if (form) {
        form.addEventListener('submit', e => {
          if (e.submitter === btn) {
            e.preventDefault();
            e.stopImmediatePropagation();
          }
        }, true);
      }
    });
  }

  async function init() {
    // Attach handlers immediately so buttons are ready before any async work
    attachHandlers();

    new MutationObserver(() => {
      if (document.querySelector('.checkout-with-protection:not([data-protection-handled]), button[name="checkout"]:not([data-reanchor-handled]):not(.checkout-with-protection), input[name="checkout"]:not([data-reanchor-handled]):not(.checkout-with-protection)')) {
        attachHandlers();
      }
    }).observe(document.body, { childList: true, subtree: true });

    // Clear any leftover protection in the background after handlers are attached
    pageCleanup = clearExistingProtectionOnInit();
    await pageCleanup;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Back from checkout can restore this page from the back-forward cache. init does not run again,
  // so the clicked button is still disabled and the protection line is still in the cart.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll(protectedSelector + ', ' + plainSelector).forEach(btn => { btn.disabled = false; });
    pageCleanup = clearExistingProtectionOnInit();
  });
})();
// ============================================================================
// SAME-DAY SHIPPING CUTOFF COUNTDOWN
// ----------------------------------------------------------------------------
// Renders a live "Ships today if you order in the next Xh Ym" message inside
// the data-inventory-base inventory span on product pages.
//
// Cutoff rules (Pacific local time, DST-aware via Intl/America/Los_Angeles):
//   - Mon–Fri: 3:00 PM cutoff
//   - Sat:     11:00 AM cutoff
//   - Sun:     no countdown (always shows fallback)
//
// When eligible:  show countdown, hide original stock text
// When not:       show original stock text, append "— ships [next business day]"
//
// Element gates (set in scs-cutoff-countdown.liquid):
//   - {% unless product.tags contains 'no-shiptimer' %} — countdown markup
//     does not render at all if product has the no-shiptimer tag, so this
//     entire script is a no-op for those products
//   - The countdown only renders inside data-inventory-base spans, which the
//     existing inventory-display logic shows ONLY when no add-on with ship
//     days is selected. So the "hide when add-ons add ship days" rule is
//     enforced by the existing span toggle — no extra wiring needed here.
// ============================================================================
(function() {
  'use strict';

  // Read from shared config injected by scs-custom-liquid.liquid (D-01/D-02).
  // Fallbacks match the previous hardcoded values — safe if deploy omits the new settings.
  const WEEKDAY_CUTOFF_HOUR  = window.scsShipConfig?.weekdayCutoffHour  ?? 15;
  const SATURDAY_CUTOFF_HOUR = window.scsShipConfig?.saturdayCutoffHour ?? 11;
  const URGENCY_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2 hours
  const TICK_INTERVAL_MS = 30 * 1000; // 30 seconds — granular enough that
                                      // the minutes display never feels stale

  // Server-time anchor: difference between server's clock and ours, in ms.
  // Computed once on init via HEAD request; all subsequent ticks use
  // Date.now() + serverOffsetMs to estimate true server time without
  // additional network calls.
  let serverOffsetMs = 0;

  /**
   * Get current "server" time as a Date — uses local clock plus the
   * one-time-fetched server offset. Avoids client clock skew issues.
   */
  function getServerNow() {
    return new Date(Date.now() + serverOffsetMs);
  }

  /**
   * Returns Pacific-time components for a given Date.
   * Uses Intl.DateTimeFormat with America/Los_Angeles to handle DST automatically.
   *
   * Returns { weekday: 0-6 (Sun=0), hour: 0-23, minute: 0-59 }
   */
  function getPacificParts(date) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false
    });
    const parts = formatter.formatToParts(date);
    const get = (type) => parts.find(p => p.type === type)?.value;

    // Map weekday short name to 0-6 (Sun=0, matching JS getDay() convention)
    const weekdayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
    const weekday = weekdayMap[get('weekday')];

    // Intl can return "24" for midnight when hour12: false — normalize to 0.
    let hour = parseInt(get('hour'), 10);
    if (hour === 24) hour = 0;

    const minute = parseInt(get('minute'), 10);

    return { weekday, hour, minute };
  }

  /**
   * Returns the cutoff hour for a given Pacific weekday, or null if no
   * same-day shipping is available that day (i.e., Sunday).
   */
  function getCutoffHour(weekday) {
    if (weekday === 0) return null;                 // Sunday — never
    if (weekday === 6) return SATURDAY_CUTOFF_HOUR; // Saturday — 11 AM
    return WEEKDAY_CUTOFF_HOUR;                     // Mon–Fri — 3 PM
  }

  /**
   * Given current Pacific parts, returns the next-business-day name as a
   * full weekday name (e.g. "Monday"). For "tomorrow" cases we still use
   * the day name rather than the word "tomorrow" — clearer and avoids
   * locale-specific phrasing.
   *
   * Service work (Pro Tune / Total Shield) only happens Mon–Fri, but this
   * countdown only ever shows when no service add-ons are selected, so we
   * only need to skip Sundays here. Saturday is a valid ship day for
   * stock-only orders.
   */
  function getNextShipDayName(pacificParts) {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday',
                      'Thursday', 'Friday', 'Saturday'];

    let nextDay = pacificParts.weekday + 1;
    if (nextDay > 6) nextDay = 0;

    // Skip Sunday (no shipping)
    if (nextDay === 0) nextDay = 1;

    return dayNames[nextDay];
  }

  /**
   * Format a millisecond duration as "Xh Ym" or "Ym" with proper plurals.
   * Always rounds UP to the next minute — saying "0m left" when there are
   * 30 seconds remaining feels broken. Saying "1m" until it actually hits
   * zero is more honest and matches user expectations.
   */
  function formatRemaining(ms) {
    const totalMinutes = Math.max(1, Math.ceil(ms / 60000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
      return hours + 'h ' + minutes + 'm';
    }
    return minutes + 'm';
  }

  /**
   * Compute milliseconds remaining until the cutoff time on the same day,
   * given current Pacific parts and the cutoff hour.
   * Returns 0 if already past cutoff.
   *
   * NOTE: This computes a Pacific-local-time duration using purely arithmetic
   * on the parts (no Date math), which is robust to DST since we're not
   * crossing a DST boundary within a single day's countdown window.
   */
  function msUntilCutoff(pacificParts, cutoffHour) {
    const currentMinutesOfDay = pacificParts.hour * 60 + pacificParts.minute;
    const cutoffMinutesOfDay = cutoffHour * 60;
    const minutesRemaining = cutoffMinutesOfDay - currentMinutesOfDay;

    if (minutesRemaining <= 0) return 0;

    // We don't have seconds in pacificParts — close enough for a 30s tick.
    return minutesRemaining * 60 * 1000;
  }

  /**
   * Update all countdown elements on the page based on current time.
   */
  function updateCountdowns() {
    const countdownEls = document.querySelectorAll('[data-cutoff-countdown]');
    if (countdownEls.length === 0) return;

    const nextDayEls = document.querySelectorAll('[data-cutoff-nextday]');

    const now = getServerNow();
    const pacific = getPacificParts(now);
    const cutoffHour = getCutoffHour(pacific.weekday);

    let isEligible = false;
    let remainingMs = 0;
    let nextDayName = null;

    if (cutoffHour !== null) {
      remainingMs = msUntilCutoff(pacific, cutoffHour);
      isEligible = remainingMs > 0;
    }

    if (!isEligible) {
      nextDayName = getNextShipDayName(pacific);
    }

    // Update countdown elements (eligible state)
    countdownEls.forEach((el) => {
      const textEl = el.querySelector('.scs-cutoff-countdown__text');
      if (!textEl) return;

      if (isEligible) {
        textEl.textContent = 'Ships today if you order in the next ' + formatRemaining(remainingMs);
        el.removeAttribute('hidden');
        el.setAttribute('data-active', '');

        if (remainingMs < URGENCY_THRESHOLD_MS) {
          el.setAttribute('data-urgent', '');
        } else {
          el.removeAttribute('data-urgent');
        }
      } else {
        el.setAttribute('hidden', '');
        el.removeAttribute('data-active');
        el.removeAttribute('data-urgent');
      }
    });

    // Update next-day fallback elements (non-eligible state)
    nextDayEls.forEach((el) => {
      if (!isEligible && nextDayName) {
        el.textContent = ' — ships ' + nextDayName;
        el.removeAttribute('hidden');
        el.setAttribute('data-active', '');
      } else {
        el.setAttribute('hidden', '');
        el.removeAttribute('data-active');
      }
    });

    // Toggle visibility of original stock text — hidden when countdown active.
    // We scope to inventory base spans only, since other inventory states
    // (with-pvc, with-tune, with-both, oversold, sold-out) don't have a
    // .scs-stock-text wrapper and shouldn't be touched.
    document.querySelectorAll('[data-inventory-base] .scs-stock-text').forEach((textEl) => {
      // Only hide stock text if THIS span has an active countdown
      const parentSpan = textEl.closest('[data-inventory-base]');
      const hasActiveCountdown = parentSpan?.querySelector('[data-cutoff-countdown][data-active]');
      if (hasActiveCountdown) {
        textEl.classList.add('scs-stock-text--hidden');
      } else {
        textEl.classList.remove('scs-stock-text--hidden');
      }
    });
  }

  /**
   * Fetch server time once via HEAD request. On failure, fall back to local
   * clock (serverOffsetMs stays at 0). The countdown is informational, not
   * security-critical, so degrading to local clock on network failure is
   * acceptable — the user's clock is usually close enough.
   */
  async function anchorServerTime() {
    try {
      const response = await fetch(window.location.href, {
        method: 'HEAD',
        cache: 'no-store'
      });
      const dateHeader = response.headers.get('date');
      if (!dateHeader) return;

      const serverDate = new Date(dateHeader);
      if (isNaN(serverDate.getTime())) return;

      serverOffsetMs = serverDate.getTime() - Date.now();
    } catch (e) {
      // Silent fallback to local clock — see note above
      console.warn('[scs-cutoff] Could not anchor server time, using local clock:', e);
    }
  }

  async function init() {
    // No-op early exit if no countdown element on this page
    if (!document.querySelector('[data-cutoff-countdown]')) return;

    // Run countdown IMMEDIATELY using local clock — this minimizes the
    // perceived flash where the user sees "In stock, ready to be shipped"
    // for a few hundred ms before it gets replaced. The HEAD request to
    // anchor server time runs in the background and silently corrects
    // on its first completion. Local clocks are typically accurate to
    // within seconds of server time, which is more than precise enough
    // for a minute-resolution countdown.
    updateCountdowns();

    // Tick every 30s. setInterval is fine for this use case — drift over a
    // single page session is negligible and we're displaying minute-resolution.
    setInterval(updateCountdowns, TICK_INTERVAL_MS);

    // Re-evaluate on variant change in case the variant selector re-renders
    // the inventory spans (it does — see product-variant-selector.liquid).
    document.addEventListener('variant:changed', () => {
      // Wait for DOM update, mirroring the pattern in the inventory toggle code above
      setTimeout(updateCountdowns, 60);
    });

    // Anchor server time in the background. When the HEAD request resolves,
    // re-run updateCountdowns once to apply the corrected offset. If the
    // user's clock was significantly skewed, this will silently update the
    // countdown text. If their clock was already accurate (typical case),
    // the update will be a no-op visually.
    anchorServerTime().then(updateCountdowns);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// phone cart drawer height
// theme.js sizes the phone drawer as window.innerHeight minus the header group. A bar above
// the header that is not in that group (an app bar injected at the top of body) is not
// counted, so the drawer ran past the bottom of the screen and clipped the checkout button.
// Size it from where it really starts, the bottom edge of .header, and give the line item
// list whatever is left after the recap.
(() => {
  const PHONE = 'screen and (max-width: 640px)';
  let queued = false;

  function setMaxHeight(el, px) {
    const value = `${px}px`;
    if (el.style.maxHeight !== value) el.style.maxHeight = value;
  }

  function fit() {
    queued = false;
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart || miniCart.getAttribute('aria-hidden') !== 'false') return;
    if (!window.matchMedia(PHONE).matches) return;
    const header = document.querySelector('.header');
    if (!header) return;

    // visualViewport is the part of the page the browser chrome leaves visible
    const vv = window.visualViewport;
    const visibleBottom = vv ? vv.offsetTop + vv.height : window.innerHeight;
    const max = Math.floor(visibleBottom - header.getBoundingClientRect().bottom);
    if (max < 1) return;

    setMaxHeight(miniCart, max);
    const content = miniCart.querySelector('.mini-cart__content');
    const recap = miniCart.querySelector('.mini-cart__recap');
    if (content && recap) setMaxHeight(content, Math.max(0, max - recap.clientHeight));
  }

  // theme.js writes its own max-height on open, resize and cart re-render; run after it
  function schedule() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(fit);
  }

  function init() {
    const miniCart = document.getElementById('mini-cart');
    if (!miniCart) return;
    try {
      new MutationObserver(schedule).observe(miniCart, {
        attributes: true, attributeFilter: ['aria-hidden', 'style'], childList: true,
      });
      // a bar that loads late moves the header down after the drawer is open
      if (window.ResizeObserver) new ResizeObserver(schedule).observe(document.body);
      window.addEventListener('resize', schedule);
      window.addEventListener('scroll', schedule, { passive: true });
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', schedule);
        window.visualViewport.addEventListener('scroll', schedule);
      }
    } catch (err) {
      console.warn('[CART-DRAWER] height fit unavailable, drawer keeps the theme height', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
